import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Grid, TableRow, TableCell, Typography, useTheme,
} from '@mui/material';
import {
  Visibility, People, Timeline, AccessTime, TrendingDown,
  FlashOn, Link as LinkIcon,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  StatCard, GlassCard, GlassTable, ShimmerSkeleton,
  EmptyState,
} from '../../../components/admin/ui';
import { getOverview } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDwell(ms) {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const PIE_COLORS = [
  ADMIN_COLORS.accentIndigo,
  ADMIN_COLORS.accentCyan,
  ADMIN_COLORS.success,
  ADMIN_COLORS.warning,
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsOverview({ filters }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      from: filters.from,
      to: filters.to,
      includeInternalRoles: filters.includeInternalRoles,
      includeBots: filters.includeBots,
    };

    getOverview(params).then((d) => {
      if (!cancelled) { setData(d); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.includeInternalRoles, filters.includeBots]);

  const kpis = data?.kpis ?? {};
  const trend = data?.trend ?? [];
  const topPages = data?.topPages ?? [];
  const topReferrers = data?.topReferrers ?? [];
  const sources = data?.sources ?? {};

  const sourcesPieData = [
    { name: 'Direct', value: sources.direct || 0 },
    { name: 'Organic', value: sources.organic || 0 },
    { name: 'Referral', value: sources.referral || 0 },
    { name: 'Campaign', value: sources.campaign || 0 },
  ].filter((d) => d.value > 0);

  const chartColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.1)';
  const textColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)';

  return (
    <Box>
      {/* ── KPI StatCards ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Visits', value: kpis.visits, icon: <Visibility />, iconIndex: 0 },
          { label: 'Unique Visitors', value: kpis.uniques, icon: <People />, iconIndex: 1 },
          { label: 'Sessions', value: kpis.sessions, icon: <Timeline />, iconIndex: 2 },
          {
            label: 'Avg Dwell',
            value: loading ? 0 : (kpis.avgDwellMs || 0),
            icon: <AccessTime />,
            iconIndex: 3,
            format: (v) => formatDwell(v),
          },
          {
            label: 'Bounce Rate',
            value: loading ? 0 : Math.round((kpis.bounceRate || 0) * 100),
            suffix: '%',
            icon: <TrendingDown />,
            iconIndex: 0,
          },
          { label: 'Active Now', value: kpis.activeNow, icon: <FlashOn />, iconIndex: 2 },
        ].map((card, i) => (
          <Grid item xs={6} sm={4} md={2} key={card.label}>
            <StatCard
              label={card.label}
              value={loading ? 0 : (card.value || 0)}
              suffix={card.suffix}
              icon={card.icon}
              iconIndex={card.iconIndex}
              loading={loading}
              index={i}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Trend LineChart ───────────────────────────────────────────── */}
      <GlassCard sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
        >
          Visits & Uniques Over Time
        </Typography>
        {loading ? (
          <ShimmerSkeleton height={220} />
        ) : trend.length === 0 ? (
          <EmptyState
            title="No trend data"
            description="Trend data will appear once traffic is recorded."
            iconIndex={0}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColor} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: textColor }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: textColor }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: isDark ? 'rgba(15,18,32,0.92)' : '#fff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="visits"
                stroke={ADMIN_COLORS.accentIndigo}
                strokeWidth={2}
                dot={false}
                name="Visits"
              />
              <Line
                type="monotone"
                dataKey="uniques"
                stroke={ADMIN_COLORS.accentCyan}
                strokeWidth={2}
                dot={false}
                name="Uniques"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </GlassCard>

      {/* ── Bottom row: Top Pages + Top Referrers + Sources Pie ──────── */}
      <Grid container spacing={2}>
        {/* Top Pages */}
        <Grid item xs={12} md={5}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Top Pages
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={220} />
            ) : topPages.length === 0 ? (
              <EmptyState title="No page data" iconIndex={0} />
            ) : (
              <GlassTable
                columns={[
                  { label: 'Path' },
                  { label: 'Views', align: 'right' },
                  { label: 'Avg Dwell', align: 'right' },
                ]}
                sx={{ maxHeight: 260 }}
              >
                {topPages.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.path_pattern}
                    </TableCell>
                    <TableCell align="right">{(row.views || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{formatDwell(row.avgDwellMs)}</TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* Top Referrers */}
        <Grid item xs={12} md={4}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Top Referrers
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={220} />
            ) : topReferrers.length === 0 ? (
              <EmptyState title="No referrer data" iconIndex={1} />
            ) : (
              <GlassTable
                columns={[
                  { label: 'Host', icon: <LinkIcon sx={{ fontSize: 14 }} /> },
                  { label: 'Sessions', align: 'right' },
                ]}
                sx={{ maxHeight: 260 }}
              >
                {topReferrers.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.host || '(direct)'}</TableCell>
                    <TableCell align="right">{(row.sessions || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* Sources Pie */}
        <Grid item xs={12} md={3}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Traffic Sources
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={220} />
            ) : sourcesPieData.length === 0 ? (
              <EmptyState title="No source data" iconIndex={2} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={sourcesPieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {sourcesPieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(v) => (
                      <span style={{ fontSize: 11, color: textColor }}>{v}</span>
                    )}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? 'rgba(15,18,32,0.92)' : '#fff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
}

AnalyticsOverview.propTypes = {
  filters: PropTypes.object.isRequired,
};
